import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { GET_FILTERED_POSITIONS } from '../graphql/queries/filteredPositions';
import { GET_TOKEN_GROUPS } from '../graphql/queries/tokenGroups';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import { START_TRANSACTIONS_EXPORT } from '../graphql/mutations/transactions';
import { GET_TRANSACTION_YEARS } from '../graphql/queries/transactions';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import PortfolioRequired from '../components/PortfolioRequired';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';
import toast from 'react-hot-toast';
import { tokenRoutePath } from '../utils/tokenRoutes';

const MARKET_CAP_OPTIONS = [
  { value: '', label: 'All Market Caps' },
  { value: 'LARGE_CAP', label: 'Large Cap' },
  { value: 'MID_CAP', label: 'Mid Cap' },
  { value: 'LOW_CAP', label: 'Low Cap' },
];

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const EXPORT_CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'SGD'];

const ExportPage = () => {
  const navigate = useNavigate();
  const { currentPortfolio } = usePortfolioContext();
  const portfolioId = currentPortfolio?.id;

  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedMarketCap, setSelectedMarketCap] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [exportType, setExportType] = useState('TRANSACTIONS');
  const [selectedYear, setSelectedYear] = useState('');
  const [includeNotes, setIncludeNotes] = useState(true);
  const [exportCurrency, setExportCurrency] = useState('');

  const filter = useMemo(() => {
    const f = { portfolioId };
    if (selectedGroup) {
      f.groupId = selectedGroup === 'none' ? 'ungrouped' : selectedGroup;
    }
    if (selectedLocation) {
      f.locationId = selectedLocation === 'none' ? 'unlocated' : selectedLocation;
    }
    if (selectedMarketCap) {
      f.marketCapCategory = selectedMarketCap;
    }
    if (searchQuery.trim()) {
      f.searchQuery = searchQuery.trim();
    }
    return f;
  }, [portfolioId, selectedGroup, selectedLocation, selectedMarketCap, searchQuery]);

  const { data: groupsData } = useQuery(GET_TOKEN_GROUPS, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  const { data: locationsData } = useQuery(GET_TOKEN_LOCATIONS, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  const { data, loading, error } = useQuery(GET_FILTERED_POSITIONS, {
    variables: { filter },
    skip: !portfolioId || exportType === 'TAX_LEDGER',
    fetchPolicy: 'cache-and-network',
  });

  const { data: yearsData, loading: yearsLoading } = useQuery(GET_TRANSACTION_YEARS, {
    variables: { portfolioId },
    skip: !portfolioId,
    fetchPolicy: 'cache-and-network',
  });

  const [startExport, { loading: exporting }] = useMutation(START_TRANSACTIONS_EXPORT);

  const tokenGroups = groupsData?.tokenGroups || [];
  const tokenLocations = locationsData?.tokenLocations || [];
  const positions = data?.filteredPositions?.positions || [];
  const totalCount = data?.filteredPositions?.totalCount || 0;
  const transactionYears = yearsData?.transactionYears || [];
  const isTaxLedgerExport = exportType === 'TAX_LEDGER';
  const effectiveYear = selectedYear || (transactionYears[0] ? String(transactionYears[0]) : '');
  const effectiveExportCurrency = exportCurrency || currentPortfolio?.currency || 'USD';

  const hasActiveFilters = selectedGroup || selectedLocation || selectedMarketCap || searchQuery.trim();

  const handleReset = () => {
    setSelectedGroup('');
    setSelectedLocation('');
    setSelectedMarketCap('');
    setSearchQuery('');
  };

  const handleExport = async () => {
    if (!portfolioId) return;

    try {
      if (isTaxLedgerExport && !effectiveYear) {
        toast.error('Choose a tax year before starting the ledger export.');
        return;
      }

      const exportFilter = { portfolioId };
      if (selectedGroup) {
        exportFilter.groupId = selectedGroup === 'none' ? 'ungrouped' : selectedGroup;
      }
      if (selectedLocation) {
        exportFilter.locationId = selectedLocation === 'none' ? 'unlocated' : selectedLocation;
      }
      if (selectedMarketCap) {
        exportFilter.marketCapCategory = selectedMarketCap;
      }
      if (searchQuery.trim()) {
        exportFilter.searchQuery = searchQuery.trim();
      }

      const hasFilters = selectedGroup || selectedLocation || selectedMarketCap || searchQuery.trim();
      const variables = isTaxLedgerExport
        ? {
            portfolioId,
            filter: null,
            exportType: 'TAX_LEDGER',
            year: Number(effectiveYear),
            includeNotes,
            exportCurrency: effectiveExportCurrency,
          }
        : { portfolioId, filter: hasFilters ? exportFilter : null, exportType: 'TRANSACTIONS', year: null };

      const { data: result } = await startExport({ variables });

      if (result?.startTransactionsExport?.success) {
        toast.success('Export started! You can track progress in Background Tasks.');
        navigate('/imports');
      } else {
        toast.error(result?.startTransactionsExport?.error || 'Failed to start export');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to start export. Please try again.');
    }
  };

  if (!portfolioId) {
    return <PortfolioRequired title="Export Transactions" description="Select a portfolio to export transactions." />;
  }

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Export Transactions</h1>
          <Link to="/transactions" className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            ← Back to Transactions
          </Link>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Export a filtered transaction CSV or a tax-office ledger for one transaction year.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0">
          <button
            onClick={handleExport}
            disabled={exporting || loading || yearsLoading || (isTaxLedgerExport && !effectiveYear)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Starting...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {isTaxLedgerExport ? `Export tax ledger${effectiveYear ? ` (${effectiveYear})` : ''}` : `Export ${totalCount > 0 ? `(${totalCount} positions)` : ''}`}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Export type
            </label>
            <select
              value={exportType}
              onChange={(e) => setExportType(e.target.value)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[190px]"
            >
              <option value="TRANSACTIONS">Transactions CSV</option>
              <option value="TAX_LEDGER">Tax office ledger</option>
            </select>
          </div>

          {isTaxLedgerExport && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tax year
              </label>
              <select
                value={effectiveYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                disabled={yearsLoading || transactionYears.length === 0}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[120px] disabled:opacity-50"
              >
                {transactionYears.length === 0 ? (
                  <option value="">No years</option>
                ) : (
                  transactionYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))
                )}
              </select>
            </div>
          )}

          {isTaxLedgerExport && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Export currency
              </label>
              <select
                value={effectiveExportCurrency}
                onChange={(e) => setExportCurrency(e.target.value)}
                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[120px]"
              >
                {EXPORT_CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
          )}

          {isTaxLedgerExport && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pb-2">
              <input
                type="checkbox"
                checked={includeNotes}
                onChange={(e) => setIncludeNotes(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
              />
              Include notes column
            </label>
          )}
        </div>

        {!isTaxLedgerExport && (
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Token name or ticker..."
              className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[150px]"
            >
              <option value="">All Groups</option>
              {tokenGroups.map((group) => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
              <option value="none">Ungrouped</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location
            </label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[150px]"
            >
              <option value="">All Locations</option>
              {tokenLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
              <option value="none">No Location</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Market Cap
            </label>
            <select
              value={selectedMarketCap}
              onChange={(e) => setSelectedMarketCap(e.target.value)}
              className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm min-w-[150px]"
            >
              {MARKET_CAP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            >
              Reset filters
            </button>
          )}
        </div>
        )}
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {isTaxLedgerExport ? (
                <>Exporting a tax-office ledger for all visible <strong>{effectiveYear || 'selected year'}</strong> transactions. Location columns are excluded; each row includes the asset balance after that transaction.</>
              ) : hasActiveFilters ? (
                <>Exporting transactions for <strong>{totalCount}</strong> filtered positions.</>
              ) : (
                <>Exporting <strong>all</strong> transactions ({totalCount} positions).</>
              )}
            </p>
          </div>
        </div>
      </div>

      {!isTaxLedgerExport && (loading && !data ? (
        <div className="py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorAlert message="Failed to load positions. Please try again." />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Token</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Group</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Holdings</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {positions.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No positions match the current filters.
                    </td>
                  </tr>
                ) : (
                  positions.map((position) => (
                    <tr key={position.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {position.token?.coingeckoApiId || position.token?.coingeckoSlug ? (
                                <Link
                                  to={tokenRoutePath(position.token)}
                                  className="hover:text-blue-600 dark:hover:text-blue-400"
                                >
                                  {position.token.ticker || '—'}
                                </Link>
                              ) : (
                                position.token?.ticker || '—'
                              )}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {position.token?.name || '—'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {position.token?.group?.name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                        {position.token?.location?.name || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 text-right tabular-nums">
                        {position.holdingsAmount ? Number(position.holdingsAmount).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 text-right tabular-nums">
                        {formatCurrency(position.holdingsValue)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ExportPage;
